import { CheckIcon } from 'lucide-react'

import { getCurrentAppIdentifier } from '../config/apps'
import { buildOrgTargetUrl } from '../store/activeOrg'
import { useAuthStore } from '../store/auth'
import type { Membership } from '../types'

export const orgTypeLabels: Record<string, string> = {
  cloud_provider: 'CP',
  reseller: 'RS',
  end_customer: 'EC',
  system: 'ADM',
}

export const orgTypeColors: Record<string, string> = {
  cloud_provider: 'bg-blue-500',
  reseller: 'bg-emerald-500',
  end_customer: 'bg-purple-500',
  system: 'bg-red-500',
}

type OrgOptionListProps = {
  /** Chamado apos uma acao de selecao (ex.: fechar o menu que contem a lista). */
  onSelect?: () => void
}

/**
 * Lista de organizacoes do membro (com tipo, papel e check da org ativa).
 * Usada dentro de menus de troca de org (ex.: UserMenu). A troca abre a
 * org escolhida em uma nova aba (?org= ativa essa org la), preservando o
 * contexto da aba atual. A logica de roteamento mora em activeOrg.ts.
 */
export function OrgOptionList({ onSelect }: OrgOptionListProps) {
  const { memberships, activeOrganizationId } = useAuthStore()

  if (!memberships || memberships.length <= 1) return null

  const handleSwitch = (m: Membership) => {
    onSelect?.()

    // Org já ativa nesta aba: nada a fazer.
    if (m.organization_id === activeOrganizationId) {
      return
    }

    const url = buildOrgTargetUrl(m, {
      origin: window.location.origin,
      currentApp: getCurrentAppIdentifier(),
    })
    window.open(url, '_blank', 'noopener')
  }

  return (
    <>
      {memberships.map((m) => (
        <button
          key={m.organization_id}
          type="button"
          onClick={() => handleSwitch(m)}
          role="menuitemradio"
          aria-checked={m.organization_id === activeOrganizationId}
          className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-700"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm text-white">{m.organization_name}</span>
              <span
                className={`${orgTypeColors[m.organization_type || ''] || 'bg-gray-500'} rounded px-1 py-0.5 text-[9px] font-bold text-white`}
              >
                {orgTypeLabels[m.organization_type || ''] || '?'}
              </span>
            </div>
            <div className="truncate text-xs text-gray-400">{m.roles.join(', ') || 'Membro'}</div>
          </div>
          {m.organization_id === activeOrganizationId && (
            <CheckIcon className="h-4 w-4 flex-shrink-0 text-emerald-400" />
          )}
        </button>
      ))}
    </>
  )
}
